import React from 'react';
import Pill, { PillTone } from './Pill';

export interface StatusMeta {
  label: React.ReactNode;
  tone: PillTone;
  /** Hover text, e.g. what the status means. */
  title?: string;
}

interface StatusPillProps<K extends string | number> {
  value: K | null | undefined;
  /** One entry per status value. Define it once per domain, next to its type,
   *  so every screen shows the same status the same way. */
  meta: Partial<Record<K, StatusMeta>>;
  /** Shown when `value` has no entry — typically the raw display string. */
  fallback?: StatusMeta;
  solid?: boolean;
  plain?: boolean;
}

/**
 * A domain status (order status, SOA state, checklist step…) rendered as a
 * Pill. The mapping from value to label and tone lives in one `meta` table
 * instead of a class name per status in each page's stylesheet.
 */
function StatusPill<K extends string | number>({ value, meta, fallback, solid, plain }: StatusPillProps<K>) {
  const entry = (value !== null && value !== undefined ? meta[value] : undefined) ?? fallback;
  if (!entry) return null;
  return (
    <Pill tone={entry.tone} solid={solid} plain={plain} title={entry.title}>
      {entry.label}
    </Pill>
  );
}

export default StatusPill;

interface ActiveStatusProps {
  active: boolean;
  activeLabel?: React.ReactNode;
  inactiveLabel?: React.ReactNode;
}

/**
 * Active / Inactive for a master record. Inactive is neutral, not red: red is
 * reserved for a loss or a failure, and a deactivated record is neither.
 */
export const ActiveStatus: React.FC<ActiveStatusProps> = ({
  active,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
}) => <Pill tone={active ? 'positive' : 'neutral'}>{active ? activeLabel : inactiveLabel}</Pill>;

interface YesNoProps {
  value: boolean | null | undefined;
  yes?: React.ReactNode;
  no?: React.ReactNode;
  /** Shown when the value is null/undefined. */
  empty?: React.ReactNode;
}

/** A boolean field shown as a plain pill. */
export const YesNo: React.FC<YesNoProps> = ({ value, yes = 'Yes', no = 'No', empty = '—' }) => {
  if (value === null || value === undefined) return <>{empty}</>;
  return <Pill tone={value ? 'info' : 'neutral'} plain>{value ? yes : no}</Pill>;
};
